import React, { useMemo, useState } from "react";
import { useLoaderData } from "react-router-dom";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { toast } from "react-toastify";
import { IsNullOrEmpty } from "../../../services/CustomService";
import {
    addGeneralQuestion,
    generateAdminToolQuestions,
    generateMissingQuestions,
    getAdminToolQuestions,
    getGeneralQuestions,
    getSatisfactionOverview,
    getSatisfactionSettings,
    reorderGeneralQuestions,
    searchSatisfactionTools,
    sendDueRemindersNow,
    updateSatisfactionSettings
} from "../../../data/admin/satisfaction";
import { deleteQuestion, updateQuestion, addToolQuestion, reorderToolQuestions } from "../../../data/loca/satisfaction";
import QuestionsManager from "../../loca/satisfaction/QuestionsManager";
import { formatScore } from "../../loca/satisfaction/SatisfactionStats";
import "../../../assets/css/satisfaction.css";

// Administration Lokapp : questions generales du questionnaire de satisfaction
// et gestion / generation des questions specifiques des outils du catalogue.

export async function loader() {
    const [overview, generalQuestions, reminderSettings] = await Promise.all([
        getSatisfactionOverview(),
        getGeneralQuestions(),
        getSatisfactionSettings().catch(() => null)
    ]);
    return { overview, generalQuestions, reminderSettings };
}

const formatDateTime = (value) => (value ? new Date(value).toLocaleString("fr-FR") : "—");

export default function AdminSatisfactionPage() {
    const data = useLoaderData();
    const [overview, setOverview] = useState(data.overview);
    const [generalQuestions, setGeneralQuestions] = useState(data.generalQuestions ?? []);
    const [generatingMissing, setGeneratingMissing] = useState(false);
    const [reminderSettings, setReminderSettings] = useState(data.reminderSettings);
    const [reminderDelay, setReminderDelay] = useState(data.reminderSettings?.reminderDelayDays ?? 3);
    const [savingReminder, setSavingReminder] = useState(false);
    const [sendingReminders, setSendingReminders] = useState(false);

    const saveReminderDelay = async () => {
        setSavingReminder(true);
        try {
            const updated = await updateSatisfactionSettings({ reminderDelayDays: Number(reminderDelay ?? 0) });
            setReminderSettings(updated);
            setReminderDelay(updated?.reminderDelayDays ?? 0);
            toast.success(updated?.reminderDelayDays > 0
                ? `Relance automatique enregistrée : ${updated.reminderDelayDays} jour${updated.reminderDelayDays > 1 ? "s" : ""} après l'invitation.`
                : "Relance automatique désactivée.");
        } catch (error) {
            toast.error(error?.message || "Enregistrement impossible.");
        } finally {
            setSavingReminder(false);
        }
    };

    const runSendReminders = async () => {
        setSendingReminders(true);
        try {
            const result = await sendDueRemindersNow();
            setReminderSettings(result?.settings ?? reminderSettings);
            toast.success(`${result?.sent ?? 0} relance${(result?.sent ?? 0) > 1 ? "s" : ""} envoyée${(result?.sent ?? 0) > 1 ? "s" : ""}.`);
        } catch (error) {
            toast.error(error?.message || "Envoi impossible.");
        } finally {
            setSendingReminders(false);
        }
    };

    const [searchType, setSearchType] = useState("latest");
    const [searchValue, setSearchValue] = useState("10");
    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [selectedTool, setSelectedTool] = useState(null);
    const [loadingTool, setLoadingTool] = useState(false);

    const searchOptions = useMemo(() => ([
        { label: "Dernières annonces", value: "latest" },
        { label: "PublicId", value: "publicId" },
        { label: "CustomUrl", value: "customUrl" },
        { label: "Email utilisateur", value: "email" }
    ]), []);

    const runGenerateMissing = async () => {
        setGeneratingMissing(true);
        try {
            const result = await generateMissingQuestions();
            setOverview(result?.overview ?? overview);
            toast.success(`Questions générées pour ${result?.count ?? 0} outil${(result?.count ?? 0) > 1 ? "s" : ""}.`);
        } catch (error) {
            toast.error(error?.message || "Génération impossible.");
        } finally {
            setGeneratingMissing(false);
        }
    };

    const handleSearch = async () => {
        if (!searchValue.trim()) {
            toast.warn("Merci d'indiquer une valeur de recherche.");
            return;
        }
        setLoadingSearch(true);
        try {
            setSearchResults((await searchSatisfactionTools(searchType, searchValue.trim())) ?? []);
        } finally {
            setLoadingSearch(false);
        }
    };

    const openTool = async (publicId) => {
        setLoadingTool(true);
        try {
            setSelectedTool(await getAdminToolQuestions(publicId));
        } catch (error) {
            toast.error(error?.message || "Outil introuvable.");
        } finally {
            setLoadingTool(false);
        }
    };

    const updateSelectedQuestions = (questions) => {
        setSelectedTool((current) => (current ? { ...current, questions } : current));
        setSearchResults((current) => current.map((tool) => (
            tool.publicId === selectedTool?.toolPublicId
                ? { ...tool, questionsCount: questions.filter((question) => question.isActive).length }
                : tool
        )));
    };

    const toolBodyTemplate = (rowData) => (
        <div className="flex align-items-center gap-2">
            {!IsNullOrEmpty(rowData.picture)
                ? <img className="smallPicture" alt={rowData.name} src={rowData.picture} />
                : <div className="no-picture no-picture-small"></div>}
            <div>
                <div><strong>{rowData.name}</strong></div>
                <small className="text-muted">{[rowData.category, rowData.subCategory].filter(Boolean).join(" · ")}</small>
            </div>
        </div>
    );

    const ownerBodyTemplate = (rowData) => (
        <div>
            <div>{rowData.ownerName}</div>
            <small className="text-muted">{rowData.ownerEmail}</small>
        </div>
    );

    const actionsBodyTemplate = (rowData) => (
        <Button
            label={rowData.questionsCount > 0 ? `Gérer (${rowData.questionsCount})` : "Aucune question"}
            icon="pi pi-list"
            size="small"
            severity={rowData.questionsCount > 0 ? "secondary" : "warning"}
            outlined
            loading={loadingTool && selectedTool?.toolPublicId === rowData.publicId}
            onClick={() => openTool(rowData.publicId)}
        />
    );

    return (
        <div>
            <h1>Satisfaction des locataires</h1>

            <div className="card flex flex-column gap-3">
                <div>
                    <h2>Vue d'ensemble</h2>
                    <p className="text-muted">
                        Un questionnaire est envoyé au locataire à la clôture de chaque location : questions générales ci-dessous + 2 à 3 questions propres au matériel.
                    </p>
                </div>
                <div className="sat-kpis">
                    <div className="sat-kpi"><span>Questionnaires</span><strong>{overview?.surveysCount ?? 0}</strong><small>{overview?.completedCount ?? 0} terminés</small></div>
                    <div className="sat-kpi"><span>Note moyenne</span><strong>{formatScore(overview?.averageScore)}</strong></div>
                    <div className="sat-kpi"><span>Outils avec questions</span><strong>{overview?.toolsWithQuestions ?? 0}</strong></div>
                    <div className="sat-kpi"><span>Outils actifs sans questions</span><strong>{overview?.activeToolsWithoutQuestions ?? 0}</strong></div>
                </div>
                <div>
                    <Button
                        label={`Générer les questions manquantes (${overview?.activeToolsWithoutQuestions ?? 0} outils)`}
                        icon="pi pi-sparkles"
                        size="small"
                        disabled={(overview?.activeToolsWithoutQuestions ?? 0) === 0}
                        loading={generatingMissing}
                        onClick={runGenerateMissing}
                    />
                    <small className="text-muted ml-2">Génération à partir du type de matériel (nom, catégorie, mots-clés). Les outils déjà dotés de questions ne sont pas modifiés.</small>
                </div>
            </div>

            <div className="card flex flex-column gap-3 mt-4">
                <div>
                    <h2>Relance automatique</h2>
                    <p className="text-muted">
                        Si un locataire n'a pas répondu après ce délai, le Worker lui envoie un unique rappel (mail « Rappel questionnaire de satisfaction », modifiable dans les paramètres), en journée uniquement. Mettez 0 pour désactiver.
                    </p>
                </div>
                <div className="flex flex-column md:flex-row gap-3 align-items-start md:align-items-center">
                    <div className="flex align-items-center gap-2">
                        <InputNumber
                            inputId="sat-reminder-delay"
                            value={reminderDelay}
                            min={0}
                            max={reminderSettings?.maxReminderDelayDays ?? 60}
                            showButtons
                            buttonLayout="horizontal"
                            decrementButtonIcon="pi pi-minus"
                            incrementButtonIcon="pi pi-plus"
                            inputStyle={{ width: "5rem" }}
                            onValueChange={(event) => setReminderDelay(event.value ?? 0)}
                        />
                        <label htmlFor="sat-reminder-delay">jour{Number(reminderDelay) > 1 ? "s" : ""} après l'invitation</label>
                    </div>
                    <Button label="Enregistrer" icon="pi pi-save" size="small" loading={savingReminder} onClick={saveReminderDelay} />
                    <Button
                        label="Envoyer les relances dues maintenant"
                        icon="pi pi-send"
                        size="small"
                        severity="secondary"
                        outlined
                        disabled={(reminderSettings?.reminderDelayDays ?? 0) === 0 || (reminderSettings?.pendingReminders ?? 0) === 0}
                        loading={sendingReminders}
                        onClick={runSendReminders}
                    />
                </div>
                <div className="sat-kpis">
                    <div className="sat-kpi"><span>Relances dues</span><strong>{reminderSettings?.pendingReminders ?? 0}</strong><small>envoyées au prochain passage du Worker</small></div>
                    <div className="sat-kpi"><span>En attente du délai</span><strong>{reminderSettings?.awaitingReminders ?? 0}</strong><small>sans aucune réponse, délai non écoulé</small></div>
                    <div className="sat-kpi"><span>Relances envoyées</span><strong>{reminderSettings?.remindersSent ?? 0}</strong><small>dernière : {formatDateTime(reminderSettings?.lastReminderSentAt)}</small></div>
                </div>
            </div>

            <div className="card flex flex-column gap-3 mt-4">
                <div>
                    <h2>Questions générales</h2>
                    <p className="text-muted">Posées à chaque location, quel que soit le matériel. La note globale sert de score principal dans les statistiques.</p>
                </div>
                <QuestionsManager
                    questions={generalQuestions}
                    onChange={setGeneralQuestions}
                    addQuestion={addGeneralQuestion}
                    updateQuestion={updateQuestion}
                    deleteQuestion={deleteQuestion}
                    reorderQuestions={reorderGeneralQuestions}
                    addLabel="Ajouter une question générale"
                />
            </div>

            <div className="card flex flex-column gap-3 mt-4">
                <div>
                    <h2>Questions par outil</h2>
                    <div className="flex flex-column md:flex-row gap-2 align-items-start md:align-items-center">
                        <Dropdown value={searchType} options={searchOptions} onChange={(event) => setSearchType(event.value)} placeholder="Type de recherche" />
                        <InputText
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            placeholder="Recherche"
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    handleSearch();
                                }
                            }}
                        />
                        <Button label="Rechercher" icon="pi pi-search" size="small" loading={loadingSearch} onClick={handleSearch} />
                    </div>
                </div>
                <DataTable value={searchResults} paginator rows={10} dataKey="publicId" showGridlines emptyMessage="Aucun résultat">
                    <Column header="Annonce" body={toolBodyTemplate} />
                    <Column header="Propriétaire" body={ownerBodyTemplate} />
                    <Column header="" body={actionsBodyTemplate} />
                </DataTable>

                {selectedTool && (
                    <div className="sat-rental-panel">
                        <h3>
                            {selectedTool.toolName}
                            <span className="sat-status">{[selectedTool.category, selectedTool.subCategory].filter(Boolean).join(" · ") || "Sans catégorie"}</span>
                        </h3>
                        <p className="text-muted">
                            {selectedTool.ownerName}{selectedTool.ownerEmail ? ` · ${selectedTool.ownerEmail}` : ""}
                            {selectedTool.familyKey && selectedTool.familyKey !== "existant" ? ` · famille de génération : ${selectedTool.familyKey}` : ""}
                        </p>
                        <QuestionsManager
                            questions={selectedTool.questions ?? []}
                            onChange={updateSelectedQuestions}
                            addQuestion={(input) => addToolQuestion(selectedTool.toolPublicId, input)}
                            updateQuestion={updateQuestion}
                            deleteQuestion={deleteQuestion}
                            reorderQuestions={(publicIds) => reorderToolQuestions(selectedTool.toolPublicId, publicIds)}
                            generateQuestions={async (ia) => {
                                const result = await generateAdminToolQuestions(selectedTool.toolPublicId, ia);
                                setSelectedTool(result);
                                return { questions: result?.questions ?? [] };
                            }}
                            allowGenerate
                            allowIA
                            emptyMessage="Aucune question pour cet outil."
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
